/**
 * Inline style factories built from `@dayvise/design-tokens` (prompt.md §40).
 *
 * WHY factories instead of a CSS framework or a stylesheet: the same components
 * are rendered by the Vite web app and by the Tauri desktop shell, and both must
 * theme from the *same* `--dayvise-*` custom properties. Reading tokens through
 * custom properties means a scheme switch is one attribute on a root element,
 * and no component ever hardcodes a hex value.
 */

import type { CSSProperties } from "react";

import { Z_INDEX } from "@dayvise/design-tokens";

export type ControlVariant = "primary" | "secondary" | "ghost" | "danger";
export type ControlSize = "sm" | "md" | "lg";

export const CONTROL_HEIGHTS: Readonly<Record<ControlSize, string>> = {
  sm: "28px",
  md: "34px",
  lg: "42px",
};

/**
 * Colour per variant. `hover`/`active` are handed to the stylesheet through a
 * custom property, which is how a control keeps its variant logic in TypeScript
 * while the interactive state itself stays in CSS.
 */
export function buttonPalette(variant: ControlVariant): { background: string; border: string; color: string; hover: string; active: string } {
  switch (variant) {
    case "primary":
      return {
        background: "var(--dayvise-color-accent)",
        border: "1px solid var(--dayvise-color-accent)",
        color: "var(--dayvise-color-on-accent)",
        hover: "color-mix(in srgb, var(--dayvise-color-accent) 88%, var(--dayvise-color-text-primary))",
        active: "color-mix(in srgb, var(--dayvise-color-accent) 76%, var(--dayvise-color-text-primary))",
      };
    case "danger":
      return {
        background: "transparent",
        border: "1px solid color-mix(in srgb, var(--dayvise-color-danger) 55%, transparent)",
        color: "var(--dayvise-color-danger)",
        hover: "color-mix(in srgb, var(--dayvise-color-danger) 12%, transparent)",
        active: "color-mix(in srgb, var(--dayvise-color-danger) 20%, transparent)",
      };
    case "ghost":
      return {
        background: "transparent",
        border: "1px solid transparent",
        color: "var(--dayvise-color-text-primary)",
        hover: "color-mix(in srgb, var(--dayvise-color-text-primary) 8%, transparent)",
        active: "color-mix(in srgb, var(--dayvise-color-text-primary) 14%, transparent)",
      };
    default:
      return {
        background: "var(--dayvise-color-raised)",
        border: "1px solid var(--dayvise-color-border)",
        color: "var(--dayvise-color-text-primary)",
        hover: "color-mix(in srgb, var(--dayvise-color-text-primary) 6%, var(--dayvise-color-raised))",
        active: "color-mix(in srgb, var(--dayvise-color-text-primary) 10%, var(--dayvise-color-raised))",
      };
  }
}

/** Just the interactive colours, for a control that styles itself. */
export function hoverPalette(variant: ControlVariant): { hover: string; active: string } {
  const palette = buttonPalette(variant);
  return { hover: palette.hover, active: palette.active };
}

/**
 * The focus ring, as a box-shadow value.
 *
 * One string, used by every focusable control, so a keyboard user sees the same
 * treatment on a button, a field and a menu item (prompt.md §37).
 */
export const FOCUS_RING = "0 0 0 3px color-mix(in srgb, var(--dayvise-color-accent) 35%, transparent)";

/** No ring: used when a control blurs and must return to its resting state. */
export const NO_RING = "none";

/** Border colour a field shows while focused. */
export const FOCUS_BORDER = "var(--dayvise-color-accent)";

/** The class names the stylesheet defines, re-exported for components. */
export const CONTROL_CLASS = "dayvise-control";
export const FOCUSABLE_CLASS = "dayvise-focusable";

export function controlStyle(input: {
  readonly variant?: ControlVariant;
  readonly size?: ControlSize;
  readonly disabled?: boolean;
  readonly fullWidth?: boolean;
  readonly iconOnly?: boolean;
}): CSSProperties {
  const variant = input.variant ?? "secondary";
  const size = input.size ?? "md";
  const palette = buttonPalette(variant);
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--dayvise-space-xs)",
    height: CONTROL_HEIGHTS[size],
    minWidth: input.iconOnly ? CONTROL_HEIGHTS[size] : undefined,
    padding: input.iconOnly ? "0" : size === "sm" ? "0 var(--dayvise-space-sm)" : "0 var(--dayvise-space-md)",
    width: input.fullWidth ? "100%" : undefined,
    fontFamily: "var(--dayvise-font-family-sans)",
    fontSize: size === "lg" ? "var(--dayvise-font-size-md)" : "var(--dayvise-font-size-sm)",
    fontWeight: "var(--dayvise-font-weight-medium)",
    lineHeight: "1",
    borderRadius: "var(--dayvise-radius-md)",
    background: palette.background,
    border: palette.border,
    color: palette.color,
    cursor: input.disabled ? "not-allowed" : "pointer",
    opacity: input.disabled ? 0.55 : 1,
    WebkitTapHighlightColor: "transparent",
  };
}

export function fieldStyle(input: { readonly invalid?: boolean; readonly disabled?: boolean } = {}): CSSProperties {
  return {
    width: "100%",
    boxSizing: "border-box",
    minHeight: CONTROL_HEIGHTS.md,
    padding: "var(--dayvise-space-xs) var(--dayvise-space-sm)",
    fontFamily: "var(--dayvise-font-family-sans)",
    fontSize: "var(--dayvise-font-size-md)",
    lineHeight: "var(--dayvise-line-height-normal)",
    color: "var(--dayvise-color-text-primary)",
    background: "var(--dayvise-color-raised)",
    border: `1px solid ${input.invalid ? "var(--dayvise-color-danger)" : "var(--dayvise-color-border)"}`,
    borderRadius: "var(--dayvise-radius-md)",
    outline: "none",
    opacity: input.disabled ? 0.6 : 1,
  };
}

export function cardStyle(input: { readonly elevated?: boolean; readonly padded?: boolean } = {}): CSSProperties {
  return {
    background: "var(--dayvise-color-raised)",
    border: "1px solid var(--dayvise-color-border)",
    borderRadius: "var(--dayvise-radius-lg)",
    boxShadow: input.elevated ? "var(--dayvise-elevation-medium-shadow)" : "var(--dayvise-elevation-low-shadow)",
    padding: input.padded === false ? "0" : "var(--dayvise-space-lg)",
    fontFamily: "var(--dayvise-font-family-sans)",
    color: "var(--dayvise-color-text-primary)",
  };
}

export function overlayStyle(): CSSProperties {
  return {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "var(--dayvise-space-lg)",
    background: "var(--dayvise-color-overlay)",
    zIndex: Z_INDEX.modal,
  };
}

export function stackStyle(gap: string = "var(--dayvise-space-sm)", align: CSSProperties["alignItems"] = "stretch"): CSSProperties {
  return { display: "flex", flexDirection: "column", gap, alignItems: align };
}

export function rowStyle(gap: string = "var(--dayvise-space-sm)", justify: CSSProperties["justifyContent"] = "flex-start"): CSSProperties {
  return { display: "flex", alignItems: "center", gap, justifyContent: justify, flexWrap: "wrap" };
}

/** Muted secondary text, used for hints, timestamps and metadata. */
export const MUTED_TEXT: CSSProperties = {
  color: "var(--dayvise-color-text-muted)",
  fontSize: "var(--dayvise-font-size-sm)",
};

export const LABEL_TEXT: CSSProperties = {
  display: "block",
  color: "var(--dayvise-color-text-primary)",
  fontSize: "var(--dayvise-font-size-sm)",
  fontWeight: "var(--dayvise-font-weight-medium)",
  marginBottom: "var(--dayvise-space-xxs)",
};

export function chipStyle(color: string, background: string): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--dayvise-space-xxs)",
    padding: "2px var(--dayvise-space-sm)",
    borderRadius: "var(--dayvise-radius-pill)",
    border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
    background,
    color,
    fontFamily: "var(--dayvise-font-family-sans)",
    fontSize: "var(--dayvise-font-size-xs)",
    fontWeight: "var(--dayvise-font-weight-medium)",
    lineHeight: "1.4",
    whiteSpace: "nowrap",
  };
}
